import { useState } from 'react';

/**
 * useFrozenStock — Round 84k Phase C (May 2026).
 *
 * Pins a stock object on first render so downstream calculations stay
 * stable even when Massive's live WebSocket pushes a new minute-bar
 * price into the parent's stock cache. Used by calculator-style
 * surfaces where mid-session price drift would silently shift the
 * user's projections:
 *
 *   • DRIP Calculator tab (Stock Modal)
 *
 * The PORTFOLIO, DASHBOARD, SCREENER, and STOCK-MODAL HEADER surfaces
 * deliberately do NOT use this hook — they should reflect the freshest
 * price the moment the WS streams a new bar.
 *
 * Usage:
 *   const [frozenStock, repin] = useFrozenStock(stock);
 *   // …use frozenStock everywhere instead of stock
 *   <button onClick={repin}>Refresh price</button>
 *
 * @param {object|null|undefined} stock — the latest stock object from
 *   the parent. Captured on the first render where `stock` is truthy
 *   AND has a numeric `.price`, and re-captured whenever `repin()`
 *   is invoked.
 * @returns {[object, () => void]} — tuple of [frozenStock, repin].
 */
export default function useFrozenStock(stock) {
  // useState so calling `repin()` schedules a re-render. We seed lazily
  // with the FIRST render's stock that has a valid price; subsequent
  // prop changes flow into `stock` but stay invisible to consumers
  // until they invoke `repin()`.
  const [frozen, setFrozen] = useState(() => {
    if (stock && typeof stock.price === 'number' && stock.price > 0) {
      return stock;
    }
    return null;
  });

  // If we never managed to capture a snapshot on first render (e.g.
  // parent's price was null then), grab the first valid one we see.
  // This is a one-shot effect-free guard — no `useEffect` needed
  // because React allows state setters during render *only via this
  // exact pattern*: compare prev → set if changed.
  if (frozen === null && stock && typeof stock.price === 'number' && stock.price > 0) {
    setFrozen(stock);
  }

  const repin = () => {
    if (stock && typeof stock.price === 'number' && stock.price > 0) {
      setFrozen(stock);
    }
  };

  return [frozen || stock, repin];
}
